// =============================================================================
// features/crm/triage.ts — Fila de triagem do CRM (F55-S07).
//
// Lead SEM cidade E SEM responsável fica fora da fila de qualquer cidade. A
// fila de triagem lista só esses leads para quem tem `leads:triage` (decisão
// D12: administração e gestão geral) e a ação de triagem dá destino a eles.
//
// Endpoints:
//   GET  /api/leads/triage           — fila paginada (leads:read + leads:triage)
//   POST /api/leads/:id/triage       — define cidade e/ou responsável
//                                      (leads:write + leads:triage; entregar a
//                                      outra pessoa exige leads:transfer)
//
// Nunca useEffect + fetch — sempre TanStack Query.
// LGPD: a fila tem contrato PRÓPRIO e mínimo (`TriageLeadItem`): o servidor já
// devolve o telefone MASCARADO (`phone_masked`) e não manda e-mail, documento,
// notas nem metadata. A tela exibe o que vem — o número inteiro nunca chega
// ao navegador nesta visão (security review do PR #806).
// =============================================================================

import type { LeadResponse } from '@elemento/shared-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '../../lib/api';

/** Permissão que libera a fila (migration 0165). */
export const LEAD_TRIAGE_PERMISSION = 'leads:triage';

/** Item da fila — espelha `LeadTriageItemSchema` (apps/api leads/schemas.ts). */
export interface TriageLeadItem {
  id: string;
  name: string;
  /** Mascarado pelo servidor: `+55 69 ****-4321`. */
  phone_masked: string;
  source: LeadResponse['source'];
  created_at: string;
}

export interface TriageListResponse {
  data: TriageLeadItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface TriageListParams {
  page: number;
  limit: number;
  search?: string;
}

export interface TriageLeadInput {
  leadId: string;
  /** Cidade de destino. Omitido = não muda. */
  cityId?: string;
  /** `agents.id` do responsável. Omitido = não muda. */
  agentId?: string;
}

export const TRIAGE_QUERY_KEYS = {
  // Prefixo ['leads'] de propósito: invalidar 'leads' atualiza lista e fila.
  all: ['leads', 'triage'] as const,
  list: (params: TriageListParams) => [...TRIAGE_QUERY_KEYS.all, params] as const,
};

// ─── Lógica pura (testável sem DOM) ───────────────────────────────────────────

/**
 * Visão efetiva do CRM. Sem `leads:triage`, `?view=triagem` (link colado,
 * permissão revogada) cai na lista — o servidor também recusaria (403).
 */
export function resolveCrmView(requested: string | null, canTriage: boolean): string {
  const view = requested ?? 'lista';
  return view === 'triagem' && !canTriage ? 'lista' : view;
}

/** O que a tela faz com um erro da ação de triagem. */
export type TriageErrorOutcome =
  | { kind: 'stale' } // outro triador chegou antes: avisar e fechar
  | { kind: 'message'; message: string }; // mostrar no diálogo

function detailsCode(err: ApiError): string | null {
  const details = err.details;
  if (typeof details !== 'object' || details === null || !('code' in details)) return null;
  const code: unknown = details.code;
  return typeof code === 'string' ? code : null;
}

export function triageErrorOutcome(err: unknown): TriageErrorOutcome {
  if (err instanceof ApiError) {
    if (err.status === 409) return { kind: 'stale' };
    if (detailsCode(err) === 'LEAD_TRANSFER_FORBIDDEN') {
      return {
        kind: 'message',
        message:
          'Atribuir o lead a outra pessoa exige a permissão de transferir leads. ' +
          'Você pode assumi-lo para si ou só definir a cidade.',
      };
    }
    if (err.status === 422) {
      return {
        kind: 'message',
        message: 'A cidade ou o responsável escolhido não está mais ativo. Escolha outro.',
      };
    }
    if (err.status === 403) {
      return { kind: 'message', message: 'Você não tem permissão para triar leads.' };
    }
  }
  return { kind: 'message', message: 'Não foi possível destinar o lead. Tente novamente.' };
}

// ─── HTTP ─────────────────────────────────────────────────────────────────────

export async function fetchTriageLeads(params: TriageListParams): Promise<TriageListResponse> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);
  return api.get<TriageListResponse>(`/api/leads/triage?${qs.toString()}`);
}

export async function triageLead(input: TriageLeadInput): Promise<LeadResponse> {
  return api.post<LeadResponse>(`/api/leads/${encodeURIComponent(input.leadId)}/triage`, {
    ...(input.cityId !== undefined ? { city_id: input.cityId } : {}),
    ...(input.agentId !== undefined ? { agent_id: input.agentId } : {}),
  });
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useTriageLeads(
  params: TriageListParams,
  options: { enabled: boolean },
): {
  data: TriageListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: TRIAGE_QUERY_KEYS.list(params),
    queryFn: () => fetchTriageLeads(params),
    enabled: options.enabled,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
  return {
    data,
    isLoading,
    isError,
    refetch: () => {
      void refetch();
    },
  };
}

export function useTriageLead(): {
  mutateAsync: (input: TriageLeadInput) => Promise<LeadResponse>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: triageLead,
    // Sucesso ou 409 (outro triador chegou antes): a fila mudou nos dois casos.
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
  return { mutateAsync, isPending };
}
